import React from "react";
import {
  Box,
  Button,
  Stack,
  Tooltip,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

interface ExportTable {
  rows: string[][];
}

const EXCLUDED_PATHS = new Set(["/create-user", "/user-management"]);

const collapseWhitespace = (value: string) =>
  value.replace(/\s+/g, " ").trim();

const getPreferredFormRoot = (): HTMLElement | null => {
  const appRoot = document.querySelector("main.App");
  if (!appRoot) {
    return null;
  }

  const containers = Array.from(
    appRoot.querySelectorAll<HTMLElement>(".MuiContainer-root")
  );

  if (containers.length > 0) {
    return containers[containers.length - 1];
  }

  return appRoot as HTMLElement;
};

const getRadioValue = (cell: HTMLElement): string => {
  const checked = cell.querySelector<HTMLInputElement>(
    'input[type="radio"]:checked'
  );

  if (!checked) {
    return "";
  }

  const labelText = collapseWhitespace(
    checked.closest("label")?.textContent ||
      checked.parentElement?.textContent ||
      checked.value ||
      ""
  );

  return labelText;
};

const getInputValue = (cell: HTMLElement): string => {
  const input = cell.querySelector<HTMLInputElement>(
    'input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"])'
  );
  if (input?.value) {
    return collapseWhitespace(input.value);
  }

  const textarea = cell.querySelector<HTMLTextAreaElement>("textarea");
  if (textarea?.value) {
    return collapseWhitespace(textarea.value);
  }

  const select = cell.querySelector<HTMLSelectElement>("select");
  if (select) {
    const option = select.options[select.selectedIndex];
    const selectedValue = option?.text || select.value;
    return collapseWhitespace(selectedValue);
  }

  return "";
};

const getCellText = (cell: HTMLTableCellElement | HTMLTableHeaderCellElement) => {
  const radioValue = getRadioValue(cell);
  if (radioValue) {
    return radioValue;
  }

  const inputValue = getInputValue(cell);
  if (inputValue) {
    return inputValue;
  }

  return collapseWhitespace(cell.textContent || "");
};

const collectFormTables = (): ExportTable[] => {
  const formRoot = getPreferredFormRoot();
  if (!formRoot) {
    return [];
  }

  const tables = Array.from(formRoot.querySelectorAll<HTMLTableElement>("table"));

  return tables
    .map((table) => {
      const rows = Array.from(table.querySelectorAll("tr"))
        .map((row) =>
          Array.from(row.querySelectorAll("th,td")).map((cell) =>
            getCellText(cell as HTMLTableCellElement)
          )
        )
        .filter((row) => row.some((value) => value.length > 0));

      return { rows };
    })
    .filter((table) => table.rows.length > 0);
};

const getFormTitle = () => {
  const formRoot = getPreferredFormRoot();
  if (!formRoot) {
    return "inspection-form";
  }

  const heading =
    formRoot.querySelector("h1, h2, h3, h4")?.textContent ||
    "inspection-form";
  return collapseWhitespace(heading);
};

const sanitizeFileName = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "inspection-form";

const escapeCsv = (value: string) => {
  const escaped = value.replace(/"/g, '""');
  if (/[",\n]/.test(escaped)) {
    return `"${escaped}"`;
  }
  return escaped;
};

const buildCsv = (tables: ExportTable[]) => {
  const lines: string[] = [];
  const hasMultipleTables = tables.length > 1;

  tables.forEach((table, index) => {
    if (hasMultipleTables) {
      lines.push(`Table ${index + 1}`);
    }

    table.rows.forEach((row) => {
      lines.push(row.map(escapeCsv).join(","));
    });

    lines.push("");
  });

  return lines.join("\n");
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildPrintableHtml = (
  title: string,
  generatedOn: string,
  tables: ExportTable[]
) => {
  const tableMarkup = tables
    .map((table, index) => {
      const [firstRow = [], ...otherRows] = table.rows;

      return `
        <section>
          ${tables.length > 1 ? `<h3>Table ${index + 1}</h3>` : ""}
          <table>
            <thead>
              <tr>${firstRow.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${otherRows
                .map(
                  (row) =>
                    `<tr>${row
                      .map((cell) => `<td>${escapeHtml(cell)}</td>`)
                      .join("")}</tr>`
                )
                .join("")}
            </tbody>
          </table>
        </section>
      `;
    })
    .join("");

  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 24px;
            color: #1f2937;
          }
          h1 {
            margin: 0 0 6px;
            font-size: 20px;
          }
          p {
            margin: 0 0 18px;
            color: #4b5563;
            font-size: 12px;
          }
          h3 {
            margin: 20px 0 8px;
            font-size: 15px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 16px;
            table-layout: fixed;
            font-size: 11px;
          }
          th,
          td {
            border: 1px solid #d1d5db;
            padding: 6px;
            text-align: left;
            vertical-align: top;
            word-break: break-word;
          }
          th {
            background: #f3f4f6;
            font-weight: 700;
          }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(title)}</h1>
        <p>Generated on ${escapeHtml(generatedOn)}</p>
        <p>If the print dialog does not open automatically, press Cmd/Ctrl + P.</p>
        ${tableMarkup}
      </body>
    </html>
  `;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const link = document.createElement("a");
  const objectUrl = URL.createObjectURL(blob);
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

const FormExportActions: React.FC = () => {
  const { pathname } = useLocation();

  if (EXCLUDED_PATHS.has(pathname)) {
    return null;
  }

  const exportExcel = () => {
    const tables = collectFormTables();
    if (tables.length === 0) {
      toast.warning("No form data found to export.");
      return;
    }

    const baseName = sanitizeFileName(getFormTitle());
    const csvContent = buildCsv(tables);
    const blob = new Blob(["\ufeff", csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    downloadBlob(blob, `${baseName}.csv`);
    toast.success("Excel export downloaded.");
  };

  const exportPdf = () => {
    const tables = collectFormTables();
    if (tables.length === 0) {
      toast.warning("No form data found to export.");
      return;
    }

    const title = getFormTitle();
    const generatedOn = new Date().toLocaleString();
    const printableHtml = buildPrintableHtml(title, generatedOn, tables);
    const blob = new Blob([printableHtml], {
      type: "text/html;charset=utf-8",
    });
    const blobUrl = URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, "_blank");

    if (!printWindow) {
      URL.revokeObjectURL(blobUrl);
      toast.error("Unable to open PDF export. Please allow pop-ups.");
      return;
    }

    const triggerPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch (error) {
        console.error("PDF print trigger failed:", error);
      }
    };

    printWindow.addEventListener("load", () => {
      setTimeout(triggerPrint, 300);
    });

    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 60000);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        right: { xs: 12, md: 20 },
        bottom: { xs: 12, md: 20 },
        zIndex: 1200,
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Tooltip title="Open printable PDF view for the current form">
          <Button
            variant="contained"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportPdf}
          >
            Export PDF
          </Button>
        </Tooltip>
        <Tooltip title="Download current form table as Excel-compatible CSV">
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<TableChartIcon />}
            onClick={exportExcel}
            sx={{
              bgcolor: "white",
              borderColor: "rgba(0, 0, 0, 0.23)",
              "&:hover": { bgcolor: "#f7f7f7" },
            }}
          >
            Export Excel
          </Button>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default FormExportActions;
